import React, { useState, useEffect, useRef } from 'react'
import { getConfig, submitApplication, uploadFileToBlob, formatFileSize, isDeadlinePassed, DEFAULT_CONFIG } from '../services/api'

// ===== Icons (inline SVGs to avoid extra deps) =====
const UploadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
)

const CheckIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)

// ===== File Upload Component =====
function FileUpload({ id, label, accept, maxSizeMB, required, file, onFileChange, helper }) {
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef()

  const handleFile = (f) => {
    setError('')
    if (!f) return
    const maxBytes = maxSizeMB * 1024 * 1024
    if (f.size > maxBytes) {
      setError(`File exceeds maximum size of ${maxSizeMB}MB. Your file is ${formatFileSize(f.size)}.`)
      return
    }
    onFileChange(f)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer?.files?.[0]
    if (f) handleFile(f)
  }

  return (
    <div className="form-group">
      <label>{label} {required && <span className="required">*</span>}</label>
      {!file ? (
        <div
          className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <div className="upload-zone__icon"><UploadIcon /></div>
          <p className="upload-zone__text">
            <strong>Click to upload</strong> or drag and drop
          </p>
          <p className="upload-zone__hint">
            {accept.replace(/\./g, '').toUpperCase()} • Max {maxSizeMB >= 1024 ? Math.round(maxSizeMB / 1024) + 'GB' : maxSizeMB + 'MB'}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            style={{ display: 'none' }}
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          />
        </div>
      ) : (
        <div className="upload-preview">
          <span className="upload-preview__name">{file.name}</span>
          <span className="upload-preview__size">{formatFileSize(file.size)}</span>
          <button className="upload-preview__remove" onClick={() => onFileChange(null)}>
            Remove
          </button>
        </div>
      )}
      {error && <span className="error-text">{error}</span>}
      {helper && <span className="helper-text">{helper}</span>}
    </div>
  )
}

// ===== Consent Checkbox Component =====
function ConsentCheckbox({ statement, checked, onChange, error }) {
  return (
    <div className={`consent-box${error ? ' consent-box--error' : ''}`}>
      <p className="consent-box__statement">
        {statement} <span className="required">*</span>
      </p>
      <label className="consent-box__label">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>I understand the above statement.</span>
      </label>
      {error && <span className="error-text" style={{ display: 'block', marginTop: '0.5rem' }}>{error}</span>}
    </div>
  )
}

// ===== Upload Progress Display =====
function UploadProgress({ label, progress }) {
  return (
    <div className="upload-progress" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{label}</span>
        <span className="upload-progress__text" style={{ margin: 0 }}>{Math.round(progress)}%</span>
      </div>
      <div className="upload-progress__bar">
        <div className="upload-progress__fill" style={{ width: `${progress}%` }} />
      </div>
    </div>
  )
}


// ===== MAIN FORM PAGE =====
export default function FormPage() {
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [configLoaded, setConfigLoaded] = useState(false)
  const [formData, setFormData] = useState({})
  const [logoFile, setLogoFile] = useState(null)
  const [videoFile, setVideoFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [uploadStage, setUploadStage] = useState('') // 'data' | 'logo' | 'video'
  const [uploadProgress, setUploadProgress] = useState({ logo: 0, video: 0 })
  const [submitError, setSubmitError] = useState('')
  const [checkTop75, setCheckTop75] = useState(false)
  const [checkConsent, setCheckConsent] = useState(false)
  const [checkCsuite, setCheckCsuite] = useState(false)

  useEffect(() => {
    getConfig().then(c => {
      setConfig(c)
      setConfigLoaded(true)
    })
  }, [])

  const deadlinePassed = isDeadlinePassed(config.submissionDeadline)
  const isClosed = !config.submissionsOpen || deadlinePassed

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }))
    if (errors[fieldId]) {
      setErrors(prev => { const next = { ...prev }; delete next[fieldId]; return next })
    }
  }

  const validate = () => {
    const errs = {}
    config.formFields?.forEach(field => {
      if (field.required && !formData[field.id]?.trim()) {
        errs[field.id] = `${field.label} is required`
      }
      if (field.type === 'email' && formData[field.id] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field.id])) {
        errs[field.id] = 'Please enter a valid email address'
      }
    })
    if (!logoFile) errs.logo = 'Organization logo is required'
    if (!videoFile) errs.video = 'Video submission is required'
    if (!checkTop75) errs.checkTop75 = 'You must acknowledge the invitation statement'
    if (!checkConsent) errs.checkConsent = 'You must confirm consent for the video and photos'
    if (!checkCsuite) errs.checkCsuite = 'You must acknowledge the C-suite attendance requirement'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError('')
    if (!validate()) {
      // Scroll to first error
      const firstErr = document.querySelector('.error-text')
      firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setSubmitting(true)
    try {
      // Step 1: Submit form data and get upload URLs
      setUploadStage('data')
      const result = await submitApplication({
        ...formData,
        awardYear: config.awardYear,
        submittedAt: new Date().toISOString(),
        logoFileName: logoFile.name,
        logoFileSize: logoFile.size,
        videoFileName: videoFile.name,
        videoFileSize: videoFile.size,
      })

      // Step 2: Upload logo
      if (result.logoUploadUrl) {
        setUploadStage('logo')
        await uploadFileToBlob(result.logoUploadUrl, logoFile, (p) => {
          setUploadProgress(prev => ({ ...prev, logo: p }))
        })
      }

      // Step 3: Upload video
      if (result.videoUploadUrl) {
        setUploadStage('video')
        await uploadFileToBlob(result.videoUploadUrl, videoFile, (p) => {
          setUploadProgress(prev => ({ ...prev, video: p }))
        })
      }

      setSubmitted(true)
    } catch (err) {
      console.error('Submission error:', err)
      setSubmitError(err.message || 'Something went wrong. Please try again or contact us.')
    } finally {
      setSubmitting(false)
    }
  }

  // Format deadline for display (always in PST)
  const deadlineDisplay = (() => {
    try {
      const d = new Date(config.submissionDeadline)
      const dateStr = d.toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        timeZone: 'America/New_York'
      })
      const timeStr = d.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZone: 'America/New_York',
        timeZoneName: 'short'
      })
      return dateStr + ' at ' + timeStr
    } catch { return '' }
  })()

  // ===== RENDER =====
  return (
    <div className="page-wrapper">
      {/* Header */}
      <header className="site-header">
        <div className="container">
          <a href="https://saxadvisorygroup.com" className="site-header__logo" target="_blank" rel="noopener">
            <img src={config.logoUrl || "https://saxadvisorygroup.com/wp-content/uploads/2024/08/Sax_4-hor-white-5.png"} alt="SAX Advisory Group" />
          </a>
          <nav className="site-header__nav">
            <a href="https://saxadvisorygroup.com/industry-expertise/nonprofit/" target="_blank" rel="noopener">Nonprofit</a>
            <a href={`mailto:${config.contactEmail}`}>Contact</a>
          </nav>
        </div>
      </header>

      {/* Status Banner */}
      {isClosed ? (
        <div className="status-banner status-banner--closed">
          Applications are currently closed. {deadlinePassed && `The deadline was ${deadlineDisplay}.`}
        </div>
      ) : (
        <div className="status-banner status-banner--open">
          Applications are open! Deadline: {deadlineDisplay}
        </div>
      )}

      {/* Hero */}
      <section className="hero" style={config.heroImageUrl ? { padding: 'var(--space-xl) 0' } : {}}>
        {config.heroImageUrl ? (
          <div className="container">
            <div style={{ height: '480px', overflow: 'hidden', borderRadius: 'var(--radius-lg)' }}>
              <img
                src={config.heroImageUrl}
                alt={config.awardTitle}
                style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover', objectPosition: 'center' }}
              />
            </div>
          </div>
        ) : (
          <div className="container">
            <p className="hero__label">nonprofit focus</p>
            <h1>
              {config.awardTitle}
              <span>{config.awardYear}</span>
            </h1>
            <p className="hero__subtitle">{config.heroSubtitle}</p>
            <div className="hero__theme">{config.awardYear} Theme: {config.theme}</div>
            {!isClosed && (
              <p className="hero__deadline">
                Submissions due by <strong>{deadlineDisplay}</strong>
              </p>
            )}
          </div>
        )}
      </section>

      {/* Main Content */}
      <main className="form-section">
        <div className="container container--narrow">

          {/* Success Screen */}
          {submitted ? (
            <div className="form-card">
              <div className="success-screen">
                <div className="success-screen__icon"><CheckIcon /></div>
                <h2>Application Submitted!</h2>
                <p>
                  Thank you for applying to the {config.awardYear} {config.awardTitle}.
                  We've received your submission and will be in touch.
                  For questions, contact <a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a>.
                </p>
              </div>
            </div>
          ) : isClosed ? (
            /* Closed Message */
            <div className="form-card">
              <div className="success-screen">
                <h2>Applications Closed</h2>
                <p>
                  The submission window for the {config.awardYear} {config.awardTitle} has closed.
                  Thank you to everyone who applied. For questions, please contact{' '}
                  <a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a>.
                </p>
              </div>
            </div>
          ) : (
            /* Application Form */
            <div className="form-card">
              <div className="form-card__intro">
                <h2>Submit Your Application</h2>
                <div dangerouslySetInnerHTML={{ __html: config.introHtml }} />
              </div>

              {/* Theme & Video Questions */}
              <div style={{ marginBottom: 'var(--space-2xl)' }}>
                <h3 className="form-section-title">
                  {config.awardYear} Theme: {config.theme}
                </h3>
                <p style={{ color: 'var(--sax-gray-700)', fontSize: '0.95rem', marginBottom: '1rem' }}>
                  <em>{config.themeDefinition}</em>
                </p>
                <p style={{ color: 'var(--sax-gray-700)', fontSize: '0.95rem', marginBottom: '0.75rem' }}>
                  {config.videoInstructions}
                </p>
                <ol style={{ paddingLeft: '1.25rem', color: 'var(--sax-navy)' }}>
                  {config.videoQuestions?.map((q, i) => (
                    <li key={i} style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.95rem' }}>
                      {q}
                    </li>
                  ))}
                </ol>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                {/* Organization Information */}
                <h3 className="form-section-title">Organization Information</h3>
                {config.formFields?.filter(f => f.id.startsWith('org')).map(field => (
                  <div className="form-group" key={field.id}>
                    <label>
                      {field.label} {field.required && <span className="required">*</span>}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={formData[field.id] || ''}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        className={errors[field.id] ? 'error' : ''}
                        placeholder={field.placeholder || ''}
                      />
                    ) : (
                      <input
                        type={field.type || 'text'}
                        value={formData[field.id] || ''}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        className={errors[field.id] ? 'error' : ''}
                        placeholder={field.placeholder || ''}
                      />
                    )}
                    {errors[field.id] && <span className="error-text">{errors[field.id]}</span>}
                  </div>
                ))}

                {/* Contact Information */}
                <h3 className="form-section-title" style={{ marginTop: 'var(--space-xl)' }}>
                  Primary Contact
                </h3>
                {config.formFields?.filter(f => f.id.startsWith('contact')).map(field => (
                  <div className="form-group" key={field.id}>
                    <label>
                      {field.label} {field.required && <span className="required">*</span>}
                    </label>
                    <input
                      type={field.type || 'text'}
                      value={formData[field.id] || ''}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className={errors[field.id] ? 'error' : ''}
                    />
                    {errors[field.id] && <span className="error-text">{errors[field.id]}</span>}
                  </div>
                ))}

                <ConsentCheckbox
                  statement={config.consentTop75 || "In the case of us making the Top 75, please use the address and email above to send the invitation to the 2024 Founders' Award event."}
                  checked={checkTop75}
                  onChange={setCheckTop75}
                  error={errors.checkTop75}
                />

                {/* Executive Contact */}
                <h3 className="form-section-title" style={{ marginTop: 'var(--space-xl)' }}>
                  Executive Director / C-Suite Contact
                </h3>
                {config.formFields?.filter(f => f.id.startsWith('exec')).map(field => (
                  <div className="form-group" key={field.id}>
                    <label>
                      {field.label} {field.required && <span className="required">*</span>}
                    </label>
                    <input
                      type={field.type || 'text'}
                      value={formData[field.id] || ''}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className={errors[field.id] ? 'error' : ''}
                    />
                    {errors[field.id] && <span className="error-text">{errors[field.id]}</span>}
                  </div>
                ))}

                {/* File Uploads */}
                <h3 className="form-section-title" style={{ marginTop: 'var(--space-xl)' }}>
                  Uploads
                </h3>

                <FileUpload
                  id="logo"
                  label="Organization Logo"
                  accept={config.acceptedLogoTypes}
                  maxSizeMB={config.maxLogoSizeMB}
                  required={true}
                  file={logoFile}
                  onFileChange={setLogoFile}
                  helper="High-resolution file preferred (PNG, JPG, SVG)"
                />
                {errors.logo && <span className="error-text" style={{ display: 'block', marginTop: '-1rem', marginBottom: '1rem' }}>{errors.logo}</span>}

                <FileUpload
                  id="video"
                  label="Video Submission"
                  accept={config.acceptedVideoTypes}
                  maxSizeMB={config.maxVideoSizeMB}
                  required={true}
                  file={videoFile}
                  onFileChange={setVideoFile}
                  helper={`Non-professional video, no longer than 5 minutes. Max ${config.maxVideoSizeMB >= 1024 ? Math.round(config.maxVideoSizeMB / 1024) + 'GB' : config.maxVideoSizeMB + 'MB'}.`}
                />
                {errors.video && <span className="error-text" style={{ display: 'block', marginTop: '-1rem', marginBottom: '1rem' }}>{errors.video}</span>}

                <ConsentCheckbox
                  statement={config.consentVideo || "I confirm that I have received consent from the beneficiaries in the video and photos we have submitted to use and/or show at the Founders' Award event."}
                  checked={checkConsent}
                  onChange={setCheckConsent}
                  error={errors.checkConsent}
                />

                {/* Submit Error */}
                {submitError && (
                  <div style={{
                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
                    padding: '1rem', marginBottom: '1.5rem', color: '#b91c1c', fontSize: '0.9rem'
                  }}>
                    <strong>Error:</strong> {submitError}
                  </div>
                )}

                {/* Upload Progress */}
                {submitting && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    {uploadStage === 'data' && (
                      <p style={{ fontSize: '0.9rem', color: 'var(--sax-gray-700)' }}>
                        <span className="spinner" style={{ display: 'inline-block', width: 16, height: 16, verticalAlign: 'middle', marginRight: 8 }} />
                        Submitting application data...
                      </p>
                    )}
                    {(uploadStage === 'logo' || uploadProgress.logo > 0) && (
                      <UploadProgress label="Uploading logo..." progress={uploadProgress.logo} />
                    )}
                    {(uploadStage === 'video' || uploadProgress.video > 0) && (
                      <UploadProgress label="Uploading video..." progress={uploadProgress.video} />
                    )}
                  </div>
                )}

                <ConsentCheckbox
                  statement={config.consentCsuite || "I understand one (1) C-suite executive with the title of President, CEO, COO, CFO or Executive Director, or a Board member must attend the Founders' Award event on Tuesday, September 10, 2024 to receive the award should we be selected as the winner."}
                  checked={checkCsuite}
                  onChange={setCheckCsuite}
                  error={errors.checkCsuite}
                />

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn btn--primary btn--full"
                  disabled={submitting}
                  style={{ marginTop: 'var(--space-md)', fontSize: '1rem', padding: '1rem' }}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>

                <p style={{ textAlign: 'center', marginTop: 'var(--space-lg)', fontSize: '0.8rem', color: 'var(--sax-gray-500)' }}>
                  By submitting, you confirm that all information provided is accurate.
                  <br />
                  Questions? Contact <a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a>
                </p>
              </form>
            </div>
          )}

          {/* Prizes Section */}
          {config.prizes?.length > 0 && (
            <div style={{ marginTop: 'var(--space-2xl)', textAlign: 'center' }}>
              <h3 className="form-section-title" style={{ display: 'block', textAlign: 'center', border: 'none' }}>
                Prizes
              </h3>
              <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem' }}>
                {config.prizes.map((p, i) => (
                  <div key={i} style={{
                    background: 'white', borderRadius: '12px', padding: '1.5rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flex: '1 1 200px', maxWidth: '280px'
                  }}>
                    <strong style={{ color: 'var(--sax-gold-dark)', fontSize: '0.9rem' }}>{p.place}</strong>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--sax-gray-700)' }}>{p.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Event Info */}
          {config.eventDate && (
            <div style={{
              marginTop: 'var(--space-2xl)', textAlign: 'center',
              background: 'var(--sax-navy)', color: 'white', borderRadius: '12px',
              padding: 'var(--space-xl)'
            }}>
              <p style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--sax-gold)', marginBottom: '0.5rem' }}>
                Celebratory Event
              </p>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem' }}>
                {config.eventDate}
              </p>
              <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                {config.eventTime} • {config.eventLocation}
              </p>
            </div>
          )}

          {/* FAQ Link */}
          {config.faqUrl && (
            <div style={{ textAlign: 'center', marginTop: 'var(--space-xl)' }}>
              <a href={config.faqUrl} target="_blank" rel="noopener" className="btn btn--outline">
                View Frequently Asked Questions
              </a>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="site-footer">
        <div className="container">
          <div className="site-footer__content">
            <p className="site-footer__company"><strong>SAX Advisory Group</strong></p>
            <p className="site-footer__services">Advisory, Audit & Tax Services</p>
            <a href="https://www.saxadvisorygroup.com" target="_blank" rel="noopener" className="site-footer__link">www.saxadvisorygroup.com</a>
          </div>
          <div className="site-footer__powered">
            <span>Powered by </span>
            <strong>SAX Technology Advisors</strong>
            <span> © {new Date().getFullYear()} </span>
            <a href="https://www.saxtechnology.com" target="_blank" rel="noopener">www.saxtechnology.com</a>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Button */}
      <a 
        href="https://www.saxtechnology.com/legal-pages/privacy-policy" 
        target="_blank" 
        rel="noopener" 
        className="privacy-button"
      >
        SAX Technology Advisors Privacy Policy
      </a>
    </div>
  )
}
